// FAR-88 follow-up: each approved Planning Board tab locks only its own
// months. A change to the plan's modifications is blocked when ANY month it
// touches lies in an approved tab's window; the other tab stays editable.
// Must match internal/handlers/tablock.go, which enforces the same rule (409).
// Pure, so the rule is tested without a browser.
import { APPROVAL_TAB_NAMES, approvalKey, tabApproval } from './approval.js'

const KEYS = ['months_0_3', 'months_4_18']
const BOARD_TAB = { months_0_3: '3month', months_4_18: '4-12month' }

// The months a modification changes. Cell, UnitsPercent and DOSTarget change
// only the months in monthValues; any other type (a legacy Qty /
// PercentOfMinInv rule) also applies a non-zero defaultValue to every month.
const MONTHS_ONLY = new Set(['Cell', 'UnitsPercent', 'DOSTarget'])
export function modReach(...mods) {
  const set = new Set()
  let all = false
  for (const m of mods) {
    if (!m) continue
    for (const iso of Object.keys(m.monthValues || {})) set.add(iso)
    if (!MONTHS_ONLY.has(m.changeType) && m.defaultValue != null && Number(m.defaultValue) !== 0) all = true
  }
  return { months: [...set].sort(), all }
}

function addMonths(iso, n) {
  const [y, m] = iso.split('-').map(Number)
  const t = y * 12 + (m - 1) + n
  return `${Math.floor(t / 12)}-${String((t % 12) + 1).padStart(2, '0')}`
}

// An approved tab's months, inclusive; '' is an open bound. In order:
//  1. firstMonth/lastMonth stored on its approval entry;
//  2. the other tab's stored window (0–3 is everything before 4–18's first
//     month, 4–18 everything after 0–3's last);
//  3. the board's own months for the tab (fallback: { months_0_3: [iso...],
//     months_4_18: [iso...] }), outer edge open;
//  4. every month.
export function tabWindow(plan, key, fallback = {}) {
  const own = plan?.approvals?.[key]
  if (own?.firstMonth && own?.lastMonth) return { first: own.firstMonth, last: own.lastMonth }
  const other = plan?.approvals?.[key === 'months_0_3' ? 'months_4_18' : 'months_0_3']
  if (other?.firstMonth && other?.lastMonth) {
    return key === 'months_0_3' ? { first: '', last: addMonths(other.firstMonth, -1) } : { first: addMonths(other.lastMonth, 1), last: '' }
  }
  const ms = fallback[key] || []
  if (ms.length > 0) return key === 'months_0_3' ? { first: '', last: ms[ms.length - 1] } : { first: ms[0], last: '' }
  return { first: '', last: '' }
}

const inWindow = (w, iso) => (!w.first || iso >= w.first) && (!w.last || iso <= w.last)

// The approval keys approved on this plan, in tab order (legacy plan-level
// approvals read as both, as in approval.js).
export function approvedKeys(plan) {
  return KEYS.filter((k) => tabApproval(plan, BOARD_TAB[k]))
}

// The first approved tab (key) the reach touches, or null.
export function lockedKeyFor(plan, reach, fallback) {
  if (!reach || (!reach.all && reach.months.length === 0)) return null
  for (const key of approvedKeys(plan)) {
    if (reach.all) return key
    const w = tabWindow(plan, key, fallback)
    if (reach.months.some((iso) => inWindow(w, iso))) return key
  }
  return null
}

// The approved tab (key) a month is in, or null.
export function lockedKeyForMonth(plan, iso, fallback) {
  return lockedKeyFor(plan, { months: [iso], all: false }, fallback)
}

// "Months 0–3 is approved — remove the approval to edit". tabName defaults to
// the generic tab name.
export function lockMessage(key, tabName) {
  if (!key) return ''
  return `${tabName || APPROVAL_TAB_NAMES[key] || 'This tab'} is approved — remove the approval to edit`
}

// Whether the board tab on screen is approved (its grid is read-only).
export function boardTabLocked(plan, tab) {
  return !!(approvalKey(tab) && tabApproval(plan, tab))
}

// A Cell edit's months split into those an approved tab locks and the rest.
export function splitMonthValues(monthValues, plan, fallback) {
  const locked = {}
  const open = {}
  for (const [iso, v] of Object.entries(monthValues || {})) {
    if (lockedKeyForMonth(plan, iso, fallback)) locked[iso] = v
    else open[iso] = v
  }
  return { locked, open }
}

// Reverting a SKU (the "n edits" pill, Revert SKU, Revert all) takes back only
// the months no approved tab locks. Returns null (nothing to do), { delete }
// (no months left) or { monthValues } (the locked months, kept).
export function revertPlan(cell, plan, fallback) {
  if (!cell) return null
  const { locked, open } = splitMonthValues(cell.monthValues, plan, fallback)
  if (Object.keys(open).length === 0) return null
  if (Object.keys(locked).length === 0) return { delete: true }
  return { monthValues: locked }
}

// Back to Base Plan (basePlanSwitch.backToBasePlan's [{ mod, monthValues }])
// without touching a locked month: a month it would remove stays when an
// approved tab locks it; a change left with nothing to do is dropped.
export function unlockedBackToBase(changes, plan, fallback) {
  const out = []
  for (const { mod, monthValues } of changes || []) {
    const next = { ...monthValues }
    for (const [iso, v] of Object.entries(mod.monthValues || {})) {
      if (!(iso in next) && lockedKeyForMonth(plan, iso, fallback)) next[iso] = v
    }
    if (Object.keys(next).length === Object.keys(mod.monthValues || {}).length) continue
    out.push({ mod, monthValues: next })
  }
  return out
}

// A whole-plan change (switching the Base Plan, swapping the Modification
// list) changes both tabs, so either approval blocks it. Mirrors the server's
// planLockedMessage. consequence: "its Base Plan can't be changed".
export function planLockedMessage(plan, consequence) {
  const names = approvedKeys(plan).map((k) => APPROVAL_TAB_NAMES[k])
  if (names.length === 0) return ''
  if (names.length === 2) return `${names[0]} and ${names[1]} are approved, so ${consequence}. Remove both approvals first.`
  return `${names[0]} is approved, so ${consequence}. Remove the approval first.`
}

// The Modifications panel's note while a tab is approved.
export function panelLockNote(plan) {
  const keys = approvedKeys(plan)
  if (keys.length === 0) return ''
  if (keys.length === 2) return 'Both tabs are approved, so modifications can\'t be changed. Remove an approval to edit.'
  const name = APPROVAL_TAB_NAMES[keys[0]]
  return `${name} is approved, so modifications that touch its months can't be changed. Remove the approval to edit them.`
}
